import {
  formatChileanDateForChart,
  toChileanDateString,
} from "../../utils/dateUtils";
import { getDisplayDaySincePublished } from "../../utils/dailyMetricsDisplayDay";

export type DailyMetricPoint = {
  collectedAt: string | Date;
  daysSincePublished?: number;
  /** Used for post–Sept 14 elapsed-day axis labels when present. */
  publishedAt?: string | Date;
  views: number;
  likes: number;
  comments: number;
};

export type DailyMetricsXAxisMode = "days" | "calendar";

export type DailyMetricsChartPoint = {
  label: string;
  Views: number;
  Likes: number;
  Comments: number;
};

/** Primary series color for views (program metrics UI). */
export const DAILY_METRICS_VIEWS_COLOR = "#7056EB";

const LIKES_COLOR = "#ed6c02";
const COMMENTS_COLOR = "#9c27b0";

export const DAILY_METRICS_CHART_COLORS = {
  views: DAILY_METRICS_VIEWS_COLOR,
  likes: LIKES_COLOR,
  comments: COMMENTS_COLOR,
} as const;

/** Chart axis / tooltip date label in Chilean time (e.g. "15 jun"). */
export function formatDailyMetricsAxisDate(
  collectedAt: string | Date,
): string {
  return formatChileanDateForChart(collectedAt) || "—";
}

function chileanCalendarSortKey(collectedAt: string | Date): number {
  const dateStr = toChileanDateString(collectedAt);
  if (!dateStr) return 0;
  const [y, m, d] = dateStr.split("-").map(Number);
  return y! * 10000 + m! * 100 + d!;
}

function metricBucketKey(
  m: DailyMetricPoint,
  xAxisMode: DailyMetricsXAxisMode,
): { key: string; label: string; sortKey: number } {
  if (xAxisMode === "days") {
    const day = m.publishedAt
      ? getDisplayDaySincePublished(
          m.publishedAt,
          m.collectedAt,
          m.daysSincePublished,
        )
      : (m.daysSincePublished ?? 0);
    return {
      key: `d:${day}`,
      label: formatDailyMetricsAxisDate(m.collectedAt),
      sortKey: day,
    };
  }
  const chileanDay = toChileanDateString(m.collectedAt);
  return {
    key: `c:${chileanDay}`,
    label: formatDailyMetricsAxisDate(m.collectedAt),
    sortKey: chileanCalendarSortKey(m.collectedAt),
  };
}

export type DailyMetricsGrowthPctPoint = {
  label: string;
  /** Views gained in this period as % of that episode's final total views (averaged across episodes). */
  incrementalPct: number;
  /** Cumulative views at end of period as % of final total (averaged across episodes). */
  cumulativePct: number;
  /** Views gained in this period (averaged across episodes when multi-episode). */
  incrementalViews: number;
  /** Total views at end of period (averaged across episodes when multi-episode). */
  cumulativeViews: number;
  isPeakIncremental: boolean;
};

/**
 * Per episode: incremental views / final views × 100 per bucket; then average by bucket
 * across episodes to find which day contributes the most growth vs total views.
 */
export function buildGrowthPercentOfTotalChartPoints(
  episodes: Array<{ dailyMetrics: DailyMetricPoint[] }>,
  xAxisMode: DailyMetricsXAxisMode,
): DailyMetricsGrowthPctPoint[] {
  const byBucket = new Map<
    number,
    {
      label: string;
      incrementalPcts: number[];
      cumulativePcts: number[];
      incrementalViews: number[];
      cumulativeViews: number[];
    }
  >();

  for (const episode of episodes) {
    const perEpisode = new Map<number, { label: string; views: number }>();

    for (const m of episode.dailyMetrics) {
      const { sortKey, label } = metricBucketKey(m, xAxisMode);
      const prev = perEpisode.get(sortKey);
      if (!prev || m.views > prev.views) {
        perEpisode.set(sortKey, { label, views: m.views });
      }
    }

    const series = Array.from(perEpisode.entries()).sort((a, b) => a[0] - b[0]);
    if (series.length === 0) continue;

    const totalViews = series[series.length - 1]![1].views;
    if (totalViews <= 0) continue;

    let prevViews = 0;
    for (const [sortKey, { label, views }] of series) {
      const incremental = Math.max(0, views - prevViews);
      const incrementalPct = (incremental / totalViews) * 100;
      const cumulativePct = (views / totalViews) * 100;

      const bucket =
        byBucket.get(sortKey) ??
        (() => {
          const b = {
            label,
            incrementalPcts: [] as number[],
            cumulativePcts: [] as number[],
            incrementalViews: [] as number[],
            cumulativeViews: [] as number[],
          };
          byBucket.set(sortKey, b);
          return b;
        })();
      bucket.label = label;
      bucket.incrementalPcts.push(incrementalPct);
      bucket.cumulativePcts.push(cumulativePct);
      bucket.incrementalViews.push(incremental);
      bucket.cumulativeViews.push(views);
      prevViews = views;
    }
  }

  const points = Array.from(byBucket.entries())
    .sort((a, b) => a[0] - b[0])
    .map(
      ([, { label, incrementalPcts, cumulativePcts, incrementalViews, cumulativeViews }]) => ({
        label,
        incrementalPct:
          incrementalPcts.reduce((s: number, v: number) => s + v, 0) /
          incrementalPcts.length,
        cumulativePct:
          cumulativePcts.reduce((s: number, v: number) => s + v, 0) /
          cumulativePcts.length,
        incrementalViews:
          incrementalViews.reduce((s: number, v: number) => s + v, 0) /
          incrementalViews.length,
        cumulativeViews:
          cumulativeViews.reduce((s: number, v: number) => s + v, 0) /
          cumulativeViews.length,
      }),
    );

  let peakIdx = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i]!.incrementalPct > points[peakIdx]!.incrementalPct) {
      peakIdx = i;
    }
  }

  return points.map((p, i) => ({
    ...p,
    isPeakIncremental:
      points.length > 0 &&
      points[peakIdx]!.incrementalPct > 0 &&
      i === peakIdx,
  }));
}

/**
 * Growth % vs total views for a single video (content dialog).
 * Uses only that document's dailyMetrics — no multi-episode averaging.
 */
export function buildSingleVideoGrowthPercentChartPoints(
  dailyMetrics: DailyMetricPoint[],
  xAxisMode: DailyMetricsXAxisMode,
): DailyMetricsGrowthPctPoint[] {
  return buildGrowthPercentOfTotalChartPoints(
    [{ dailyMetrics }],
    xAxisMode,
  );
}

export function buildDailyMetricsChartPoints(
  metrics: DailyMetricPoint[],
  xAxisMode: DailyMetricsXAxisMode,
): DailyMetricsChartPoint[] {
  return [...metrics]
    .sort(
      (a, b) =>
        new Date(a.collectedAt).getTime() - new Date(b.collectedAt).getTime(),
    )
    .map((m) => ({
      label: formatDailyMetricsAxisDate(m.collectedAt),
      Views: m.views,
      Likes: m.likes,
      Comments: m.comments,
    }));
}

/** Per-snapshot likes/comments gained (day-over-day on cumulative dailyMetrics). */
export function buildDailyMetricsInteractionDeltaChartPoints(
  metrics: DailyMetricPoint[],
  xAxisMode: DailyMetricsXAxisMode = "calendar",
): DailyMetricsChartPoint[] {
  const sorted = [...metrics].sort(
    (a, b) => new Date(a.collectedAt).getTime() - new Date(b.collectedAt).getTime(),
  );
  if (sorted.length === 0) return [];

  // One snapshot: treat counts as gained since publish so the section can render.
  if (sorted.length === 1) {
    const m = sorted[0]!;
    return [
      {
        label: formatDailyMetricsAxisDate(m.collectedAt),
        Views: 0,
        Likes: Math.max(0, m.likes),
        Comments: Math.max(0, m.comments),
      },
    ];
  }

  let prevLikes = sorted[0]!.likes;
  let prevComments = sorted[0]!.comments;

  return sorted.slice(1).map((m) => {
    const likes = Math.max(0, m.likes - prevLikes);
    const comments = Math.max(0, m.comments - prevComments);
    prevLikes = m.likes;
    prevComments = m.comments;
    const label =
      xAxisMode === "days" && m.daysSincePublished != null
        ? formatDailyMetricsAxisDate(m.collectedAt)
        : formatDailyMetricsAxisDate(m.collectedAt);
    return {
      label,
      Views: 0,
      Likes: likes,
      Comments: comments,
    };
  });
}

export type DailyMetricsAbsoluteGrowthPoint = {
  label: string;
  incremental: number;
  cumulative: number;
  isPeakIncremental: boolean;
  date?: string;
};

/**
 * Absolute views growth for a single content dialog chart.
 * Unlike {@link buildGrowthPercentOfTotalChartPoints}, does not require the last
 * snapshot's views > 0 (IG OAuth refreshes sometimes write views:0 after Apify
 * seeded real counts). Snapshots with views:0 after we already have real counts
 * are treated as missing data and omitted (monotonic: never let cumulative drop).
 */
export function buildSingleContentViewsAbsoluteChartPoints(
  dailyMetrics: DailyMetricPoint[],
  xAxisMode: DailyMetricsXAxisMode,
): DailyMetricsAbsoluteGrowthPoint[] {
  const perBucket = new Map<
    number,
    { label: string; views: number; date: string }
  >();

  for (const m of dailyMetrics) {
    const views = Number(m.views) || 0;
    const { sortKey, label } = metricBucketKey(m, xAxisMode);
    const prev = perBucket.get(sortKey);
    if (!prev || views > prev.views) {
      perBucket.set(sortKey, {
        label,
        views,
        date: toChileanDateString(m.collectedAt),
      });
    }
  }

  const series = Array.from(perBucket.entries()).sort((a, b) => a[0] - b[0]);
  if (series.length === 0) return [];
  if (!series.some(([, { views }]) => views > 0)) return [];

  let prevCumulative = 0;
  const raw: Array<{
    label: string;
    incremental: number;
    cumulative: number;
    date: string;
  }> = [];

  for (const [, { label, views, date }] of series) {
    // Omit leading zeros until first real view count.
    if (views <= 0 && prevCumulative <= 0) continue;
    // Omit corrupted/missing views:0 after we already have counts — do not
    // plot a cliff or a pile of empty bars (can blank the whole metrics block).
    if (views <= 0 && prevCumulative > 0) continue;

    const cumulative = Math.max(prevCumulative, views);
    const incremental = Math.max(0, cumulative - prevCumulative);
    raw.push({ label, incremental, cumulative, date });
    prevCumulative = cumulative;
  }

  if (raw.length === 0) return [];

  let peakIdx = 0;
  for (let i = 1; i < raw.length; i++) {
    if (raw[i]!.incremental > raw[peakIdx]!.incremental) {
      peakIdx = i;
    }
  }

  return raw.map((p, i) => ({
    ...p,
    isPeakIncremental:
      raw.length > 0 && raw[peakIdx]!.incremental > 0 && i === peakIdx,
  }));
}

export function buildSingleContentInteractionAbsoluteChartPoints(
  dailyMetrics: DailyMetricPoint[],
  xAxisMode: DailyMetricsXAxisMode,
): DailyMetricsAbsoluteGrowthPoint[] {
  const perBucket = new Map<
    number,
    { label: string; interactions: number; date: string }
  >();

  for (const m of dailyMetrics) {
    const interactions =
      (Number(m.likes) || 0) + (Number(m.comments) || 0);
    const { sortKey, label } = metricBucketKey(m, xAxisMode);
    const prev = perBucket.get(sortKey);
    if (!prev || interactions > prev.interactions) {
      perBucket.set(sortKey, {
        label,
        interactions,
        date: toChileanDateString(m.collectedAt),
      });
    }
  }

  const series = Array.from(perBucket.entries()).sort((a, b) => a[0] - b[0]);
  if (series.length === 0) return [];

  let prevInteractions = 0;
  const raw = series.map(([, { label, interactions, date }]) => {
    const incremental = Math.max(0, interactions - prevInteractions);
    const point = {
      label,
      incremental,
      cumulative: interactions,
      date,
    };
    prevInteractions = interactions;
    return point;
  });

  let peakIdx = 0;
  for (let i = 1; i < raw.length; i++) {
    if (raw[i]!.incremental > raw[peakIdx]!.incremental) {
      peakIdx = i;
    }
  }

  return raw.map((p, i) => ({
    ...p,
    isPeakIncremental:
      raw.length > 0 && raw[peakIdx]!.incremental > 0 && i === peakIdx,
  }));
}

export type ContentDailyTrendRow = {
  date: string;
  viewsGained: number;
  interactionsGained: number;
};

export type BuildSingleContentDailyTrendRowsOpts = {
  minDate?: string;
};

/**
 * Per-content daily views/interactions gained from cumulative dailyMetrics snapshots.
 * Buckets by Chilean calendar day (collectedAt), matching corpus daily-trends semantics.
 */
export function buildSingleContentDailyTrendRows(
  dailyMetrics: DailyMetricPoint[],
  opts?: BuildSingleContentDailyTrendRowsOpts,
): ContentDailyTrendRow[] {
  const sorted = [...dailyMetrics].sort(
    (a, b) =>
      new Date(a.collectedAt).getTime() - new Date(b.collectedAt).getTime(),
  );
  if (sorted.length === 0) return [];

  const byDay = new Map<
    string,
    { viewsGained: number; interactionsGained: number }
  >();

  let prevViews = 0;
  let prevLikes = 0;
  let prevComments = 0;

  for (const snap of sorted) {
    const views = Number(snap.views) || 0;
    const likes = Number(snap.likes) || 0;
    const comments = Number(snap.comments) || 0;

    const viewsDelta = Math.max(0, views - prevViews);
    const likesDelta = Math.max(0, likes - prevLikes);
    const commentsDelta = Math.max(0, comments - prevComments);
    const interactionsDelta = likesDelta + commentsDelta;

    prevViews = views;
    prevLikes = likes;
    prevComments = comments;

    const dayKey = toChileanDateString(snap.collectedAt);
    if (!dayKey) continue;
    if (opts?.minDate && dayKey < opts.minDate) continue;

    const entry =
      byDay.get(dayKey) ?? { viewsGained: 0, interactionsGained: 0 };
    entry.viewsGained += viewsDelta;
    entry.interactionsGained += interactionsDelta;
    byDay.set(dayKey, entry);
  }

  return Array.from(byDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, metrics]) => ({
      date,
      viewsGained: metrics.viewsGained,
      interactionsGained: metrics.interactionsGained,
    }));
}

export function getDefaultProgramMetricsDateRange(): {
  startDate: string;
  endDate: string;
} {
  const nowChilean = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/Santiago" }),
  );
  const end = new Date(nowChilean);
  const start = new Date(nowChilean);
  start.setDate(start.getDate() - 90);
  const toIso = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { startDate: toIso(start), endDate: toIso(end) };
}
